import { defineComponent as _defineComponent } from 'vue'
import EmployeeEdit from "@/components/EmployeeEdit.vue";

import FloatingActionButton from "@/components/buttons/FloatingActionButton.vue";

import ContractEdit from "@/components/ContractEdit.vue";

import BankAccountEdit from "@/components/BankAccountEdit.vue";

import AddressEdit from "@/components/AddressEdit.vue";

import {
  useI18n,
} from "@/composables/useI18n";

import {
  computed,
  onMounted,
  PropType,
  ref,
  unref,
  watch,
} from "vue";

import type {
  AccountingPartner,
  Customer,
  FetchCustomersParam,
  Service,
} from "@/components/customers/scripts";

import {
  createEmptyCustomer,
  customerHasChanges,
  resetCustomerObject,
  createOrUpdateCustomer,
  fetchAccountingPartners,
  fetchCustomers,
  fetchCustomer,
  fetchCustomerServices,
  fetchDefaultProvision,
  updateCustomerState,
  selectCustomer,
  CUSTOMER_ID_STORAGE_KEY,
} from "@/components/customers/scripts";

import {
  CustomerState,
} from "@/Enums";

import {
  DEFAULT_SORT_COLUMN_FETCH_CUSTOMERS,
} from "@/components/customers/scripts/constants";

import {
  isStringAndHasValue,
  isRequired,
  isSmallerOrEqualToMaxLength,
} from "@/scripts/Common/validators/defaultValidator";

import {
  companyNameLength,
} from "@/scripts/Common/constraints/constraints";

import {
  copyObject,
  debounce
} from "@/scripts/Common/HelperFunctions";

import {
  AccountingDataEdit,
  InstallationEdit,
  VoucherEdit,
  WebsiteEdit,
} from "@/components/customers/children";

import {
  httpMethod,
} from "@/scripts/Common/api";

import {
  getNumberItemFromStorage,
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import {
  getAccessRights,
} from "@/authorization";

import {
  customerDataPolicies,
} from "@/authorization/policies";
import BankTransferDetails from "@/components/children/bankTransferDetails/BankTransferDetails.vue";

import {
  isCustomer,
} from "./scripts/typeGuards";


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomerCoreData',
  props: {
  startInCreationMode: {
    required: true,
    type: Boolean as PropType<boolean>
  }
},
  emits: [
  'onFetchError'
],
  setup(__props, { emit: emits }) {

const props = __props;

const {
  t,
} = useI18n();

const {
  hasEditRights
} = getAccessRights(customerDataPolicies);





const isCreationMode = ref<boolean>(props.startInCreationMode);
const panelExpander = ref([1]);
const customerForm = ref();

const customers = ref<Customer[]>([]);
const customerServices = ref<Service[]>();
const accountingPartners = ref<AccountingPartner[]>()
const defaultProvision = ref<number>();

const selectedCustomer = ref<Customer>(createEmptyCustomer());
const initialCustomerData = ref<Customer>(createEmptyCustomer());

const comboSelection = ref<Customer | undefined | string>();
const customerSearchInput = ref<string | undefined>();
let ignoreCustomerSearchInputChange = false; // when the customer is selected from the combobox, the customerSearchInput change that follows should not trigger a reload of the customer list

const contactAddressValid = ref(true);
const businessAddressValid = ref(true);
const contactEmployeeValid = ref(true);
const businessBankAccountValid = ref(true);
const websiteEditValid = ref(true);
const startBeforeEndDate = ref(true);
const voucherEditValid = ref(true);
const installationEditValid = ref(true);
const accountingDataValid = ref(true);
const bankTransferDetailsValid = ref(false);
const valid = ref(true);

const hasNoEditRights = !hasEditRights;

const hasCustomers = computed(() => {
  return customers.value && customers.value.length > 0;
})

const comboboxIsActive = computed(() => {
  if (!hasCustomers.value) return false;

  return !isCreationMode.value;
})

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
};

const baseValidation = computed(() => {
  return valid.value
      && contactAddressValid.value
      && businessAddressValid.value
      && contactEmployeeValid.value
      && businessBankAccountValid.value
      && websiteEditValid.value
      && startBeforeEndDate.value
      && voucherEditValid.value
      && accountingDataValid.value
      && installationEditValid.value
      && bankTransferDetailsValid.value;
})

const canSave = computed(() => {
  return customerHasChanges(unref(initialCustomerData), unref(selectedCustomer))
      && baseValidation.value;
})

const canReset = computed(() => {
  return customerHasChanges(unref(initialCustomerData), unref(selectedCustomer))
    || (hasCustomers.value && isCreationMode.value);
})

const canContract = computed(() => {
  return baseValidation.value
      && selectedCustomer.value.status === CustomerState.Interested
      && isStringAndHasValue(selectedCustomer.value.contractStart);
})

const canConfirm = computed(() => {
  return baseValidation.value
      && selectedCustomer.value.status === CustomerState.Contracted
      && isStringAndHasValue(selectedCustomer.value.contractStart)
      && selectedCustomer.value.accountingPartnerId
      && selectedCustomer.value.accountingPartnerId > 0;
})

const canCancel = computed(() => {
  return baseValidation.value
      && selectedCustomer.value.status === CustomerState.Confirmed
      && isStringAndHasValue(selectedCustomer.value.contractStart)
      && isStringAndHasValue(selectedCustomer.value.contractEnd);
})

const canChangeState = (currentState: CustomerState | undefined) => {
  switch (currentState) {
    case CustomerState.Interested: {
      return unref(canContract);
    }
    case CustomerState.Contracted: {
      return unref(canConfirm);
    }
    case CustomerState.Confirmed: {
      return unref(canCancel);
    }
    default: {
      return false;
    }
  }
}

const isCustomerCanceled = computed(() => selectedCustomer.value.status === CustomerState.Canceled);

const stateChangerButton = {
  [CustomerState.Interested]: {text: t("button_contract"), icon: "mdi-file-document-edit"},
  [CustomerState.Contracted]: {text: t("button_confirm"), icon: "mdi-account-reactivate"},
  [CustomerState.Confirmed]: {text: t("button_cancel"), icon: "mdi-archive-cancel-outline"},
  [CustomerState.Canceled]: {text: '', icon: ''}
}

const loadCustomers = async () => {

  const fetchArgs: FetchCustomersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_CUSTOMERS
  };

  if(customerSearchInput.value) fetchArgs.companyName = customerSearchInput.value;

  const {response, error} = await fetchCustomers(fetchArgs);

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  customers.value = response;
}

const loadCustomer = async (id: number) => {
  const {response, error} = await fetchCustomer(id);
  if (error) {
    onHandleFetchError({error});
    return null;
  }

  selectedCustomer.value = response as Customer;
  comboSelection.value = selectedCustomer.value;
}

const loadAndSetInitialCustomer = async () => {
  if (isCreationMode.value) {
    return onHandleSetupEmptyCustomer();
  }

  const {response, error} = await loadCustomerFromStorageOrFirst();

  if (response) {
    selectedCustomer.value = response;
    initialCustomerData.value = copyObject(selectedCustomer.value);
    comboSelection.value = selectedCustomer.value;
  } else {
    onHandleSetupEmptyCustomer();
  }

  if (error) {
    onHandleFetchError({error});
  }
}

// Tries to load customer from storage if available. If not it will load the first customer from the list of customers
const loadCustomerFromStorageOrFirst = async (): Promise<{ error?: any, response?: Customer }> => {
  const customerId = getNumberItemFromStorage(CUSTOMER_ID_STORAGE_KEY);

  const { error, response } = await fetchCustomer(customerId);

  // no customer found in storage or customer does not exist anymore, load first customer from list of all customers
  if(error){
    return fetchFirstCustomer();
  }

  return {error, response};
}

const fetchFirstCustomer = async () => {
  const {error, response} = await fetchCustomers({pageSize: 1});

  return {error, response: response?.[0]};
}

const loadAccountingPartners = async () => {
  const {response, error} = await fetchAccountingPartners();

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  accountingPartners.value = response;
}

const loadServices = async () => {
  const {response, error} = await fetchCustomerServices();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  customerServices.value = response;
}

const loadDefaultProvision = async () => {
  const {response, error} = await fetchDefaultProvision();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  defaultProvision.value = response;
}

const contractCustomer = async () => {
  const {response, error} = await updateCustomerState(unref(selectedCustomer), 'contract');

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  if (isCreationMode.value) isCreationMode.value = false;

  saveItemToStorage(response, CUSTOMER_ID_STORAGE_KEY);
  loadCustomer(selectedCustomer.value.id);
}

const confirmCustomer = async () => {
  const {error} = await updateCustomerState(unref(selectedCustomer), 'confirm');

  if (error) {
    onHandleFetchError({error});
    return;
  }

  loadCustomer(selectedCustomer.value.id);
}

const cancelCustomer = async () => {
  const {response, error} = await updateCustomerState(unref(selectedCustomer), 'cancel');

  if (error) {
    onHandleFetchError({error});
    return;
  }

  loadCustomer(selectedCustomer.value.id);
}

const changeCustomerState = async (currentState: CustomerState | undefined) => {
  switch (currentState) {
    case CustomerState.Interested: {
      await contractCustomer();
      break;
    }
    case CustomerState.Contracted: {
      await confirmCustomer();
      break;
    }
    case CustomerState.Confirmed: {
      await cancelCustomer();
      break;
    }
    default: {
      //if uncovered state or undefined return
      return;
    }
  }
}

const createCustomerInformation = async () => {
  const {response, error} = await createOrUpdateCustomer(unref(selectedCustomer), httpMethod.POST);

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  isCreationMode.value = false;
  saveItemToStorage(response, CUSTOMER_ID_STORAGE_KEY);
}

const updateCustomerInformation = async () => {
  const {response, error} = await createOrUpdateCustomer(unref(selectedCustomer), httpMethod.PUT);

  if (error || !response) {
    onHandleFetchError({error});
    return;
  }

  saveItemToStorage(response, CUSTOMER_ID_STORAGE_KEY);
}

const onHandleSaveCustomer = async () => {
    selectedCustomer.value.id === 0
        ? await createCustomerInformation()
        : await updateCustomerInformation()

    // set new state as initial state, so a reset rests to saved state
    initialCustomerData.value = copyObject(selectedCustomer.value);
    // update combobox search input, company name of selection might have changed
    customerSearchInput.value = selectedCustomer.value.companyName; // triggers loadCustomers

    const customerId = getNumberItemFromStorage(CUSTOMER_ID_STORAGE_KEY);

    if (customerId > 0) {
      loadCustomer(customerId);
    }
}

const onHandleResetCustomer = () => {
  resetCustomerObject(unref(initialCustomerData), unref(selectedCustomer));

  isCreationMode.value = false;
  panelExpander.value = [1];

  loadAndSetInitialCustomer();
}

const validate = () => {
  customerForm.value.validate();
}

const onHandleSetupEmptyCustomer = () => {
  isCreationMode.value = true;
  selectedCustomer.value = createEmptyCustomer();
  selectedCustomer.value.provision = defaultProvision.value;
  initialCustomerData.value = copyObject(selectedCustomer.value);
  validate();
}

const onHandleSelectionChange = () => {
  if(!comboSelection.value || !isCustomer(comboSelection.value)) return;

  const customerId = comboSelection.value.id;
  ignoreCustomerSearchInputChange = true;
  saveItemToStorage(customerId, CUSTOMER_ID_STORAGE_KEY);
}

watch(comboSelection, () => {
  if (!comboSelection.value || !isCustomer(comboSelection.value)) return;

  selectedCustomer.value = comboSelection.value;
  initialCustomerData.value = copyObject(selectedCustomer.value);
});

watch(() => customerSearchInput.value, debounce(() => {
  if(ignoreCustomerSearchInputChange) {
    ignoreCustomerSearchInputChange = false;
    return;
  }
  loadCustomers();
}));

onMounted(async () => {
  await loadAccountingPartners();
  await loadServices();
  await loadDefaultProvision();
  loadCustomers();
  loadAndSetInitialCustomer();
});


return { __sfc: true,t, hasEditRights, props, emits, isCreationMode, panelExpander, customerForm, customers, customerServices, accountingPartners, defaultProvision, selectedCustomer, initialCustomerData, comboSelection, customerSearchInput, ignoreCustomerSearchInputChange, contactAddressValid, businessAddressValid, contactEmployeeValid, businessBankAccountValid, websiteEditValid, startBeforeEndDate, voucherEditValid, installationEditValid, accountingDataValid, bankTransferDetailsValid, valid, hasNoEditRights, hasCustomers, comboboxIsActive, onHandleFetchError, baseValidation, canSave, canReset, canContract, canConfirm, canCancel, canChangeState, isCustomerCanceled, stateChangerButton, loadCustomers, loadCustomer, loadAndSetInitialCustomer, loadCustomerFromStorageOrFirst, fetchFirstCustomer, loadAccountingPartners, loadServices, loadDefaultProvision, contractCustomer, confirmCustomer, cancelCustomer, changeCustomerState, createCustomerInformation, updateCustomerInformation, onHandleSaveCustomer, onHandleResetCustomer, validate, onHandleSetupEmptyCustomer, onHandleSelectionChange, EmployeeEdit, FloatingActionButton, ContractEdit, BankAccountEdit, AddressEdit, CustomerState, isRequired, isSmallerOrEqualToMaxLength, companyNameLength, AccountingDataEdit, InstallationEdit, VoucherEdit, WebsiteEdit, BankTransferDetails }
}

})